import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { salesAPI } from '../backend';
import { formatPhone } from '../utils/formatPhone';

interface SaleProduct {
  sale_id: number;
  product_id: number;
  quantity: number;
  price: number;
  cost: number;
  quantity_paid?: number;
  product?: {
    id: number;
    name: string;
    image: string;
  };
}

interface Sale {
  id: number;
  client_id: number;
  delivery_method: string;
  payment_status: string;
  total: number;
  created_at: string;
  client?: {
    id: number;
    name: string;
    email: string;
    phone: string;
  };
  sale_products: SaleProduct[];
}

interface ReturnDialogProps {
  sale: Sale;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (selection: { [productId: number]: number }) => void;
}

function ReturnDialog({ sale, isOpen, onClose, onConfirm }: ReturnDialogProps) {
  const [returns, setReturns] = useState<{ [productId: number]: number }>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sale) {
      // Initialize all return quantities to 0
      const initial: { [productId: number]: number } = {};
      sale.sale_products.forEach(sp => {
        initial[sp.product_id] = 0;
      });
      setReturns(initial);
    }
  }, [isOpen, sale]);

  const handleQuantityChange = (productId: number, newQuantity: number, maxQuantity: number) => {
    const clamped = Math.max(0, Math.min(newQuantity, maxQuantity));
    setReturns(prev => ({
      ...prev,
      [productId]: clamped
    }));
  };

  const totals = useMemo(() => {
    let items = 0;
    let amount = 0;
    for (const sp of sale.sale_products) {
      const qty = returns[sp.product_id] || 0;
      items += qty;
      amount += qty * sp.price;
    }
    return { items, amount };
  }, [returns, sale.sale_products]);

  const hasSelection = totals.items > 0;

  const handleConfirm = async () => {
    if (!hasSelection) return;
    setLoading(true);
    try {
      const requests = Object.entries(returns)
        .filter(([_, qty]) => (qty || 0) > 0)
        .map(([productId, qty]) =>
          salesAPI.returnProductQuantity(sale.id, Number(productId), qty as number)
        );
      await Promise.all(requests);
      if (onConfirm) onConfirm(returns);
      onClose();
    } catch (err) {
      console.error('Error processing returns:', err);
      alert('Error al procesar la devolución. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !sale) return null;

  return (
    <div className="dialog-overlay">
      <div className="dialog payment-dialog">
        <div className="dialog-header">
          <h2>Registrar Devolución - Venta #{sale.id}</h2>
          <button className="dialog-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content">
          {/* Customer Info */}
          <div className="sale-customer">
            <h3 className="client-item name">{sale.client?.name}</h3>
            <p className="client-item">{sale.client?.email}</p>
            <p className="client-item">{formatPhone(sale.client?.phone)}</p>
          </div>

          {/* Products List for Returns */}
          <div className="products-payment-list">
            <h4>Selecciona artículos para devolución</h4>
            {sale.sale_products.map((product, index) => {
              const selectedQty = returns[product.product_id] || 0;
              const maxQty = product.quantity; // Cap to purchased quantity (no returns tracked yet)
              const lineAmount = selectedQty * product.price;
              return (
                <div key={index} className="product-payment-item">
                  <div className="product-info">
                    <div className="product-details">
                      <h5>{product.product?.name}</h5>
                      <p className="product-meta">
                        Cantidad comprada: {product.quantity} | Precio: ${product.price}
                      </p>
                    </div>
                  </div>

                  <div className="payment-controls">
                    <div className="quantity-controls">
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(product.product_id, selectedQty - 1, maxQty)}
                        disabled={selectedQty <= 0}
                      >
                        -
                      </button>
                      <span className="quantity-display">
                        {selectedQty}
                      </span>
                      <button
                        className="quantity-btn"
                        onClick={() => handleQuantityChange(product.product_id, selectedQty + 1, maxQty)}
                        disabled={selectedQty >= maxQty}
                      >
                        +
                      </button>
                    </div>
                    <div className="amount-info">
                      <span className="amount-paid">
                        Reembolso: ${lineAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="dialog-footer">
          <div style={{ marginRight: 'auto' }}>
            <span>
              Seleccionados: {totals.items} | Total a devolver: ${totals.amount.toFixed(2)}
            </span>
          </div>
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={!hasSelection || loading}
          >
            {loading ? 'Procesando...' : 'Confirmar Devolución'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReturnDialog;


